/**
 * V2 customer session — platform identity only (`customerId` from `/me` 200).
 * Boot + peer cross-tab signals use credentials `GET /api/v1/customer-account/me`.
 * Refresh uses `attemptSessionRefresh` (platform POST refresh).
 *
 * phase-csp: every tab open/remount/F5 always boots one `/me` (latch must not
 * gate boot). Login-land wire refuse stays inside {@link fetchPlatformMe}.
 * OIDC hold / R1 is owned by identityPipeline (`oidcLandingPending`) — CSP does
 * not invent ready+sessionPresent during that hold.
 *
 * G1 — when `?oidcLand=1` is present, CSP mounts {@link runOidcCookieLandMount}
 * (complete → strip + OIDC_LAND_QUERY_CHANGED_EVENT → establishSession).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  clearSameTabExplicitAuth,
  hasSameTabExplicitAuth,
  markSameTabExplicitAuth,
} from 'pi-kiosk-shared/crossTab';
import { useRuntimeConfig } from '../providers/RuntimeConfigProvider.js';
import { useTenantCode } from '../tenant/TenantProvider.js';
import {
  customerAuthBus,
  publishCustomerAuth,
} from '../crossTab/customerCrossTab.js';
import { useCustomerSessionTimeouts } from './useCustomerSessionTimeouts.js';
import {
  endCustomerSession,
  markCustomerSessionStarted,
} from './endCustomerSession.js';
import { registerCustomerAuthSyncDeps } from './customerAuthSync.js';
import {
  resolveCustomerSessionUpdate,
  type CustomerSessionApplySource,
} from './customerSessionMerge.js';
import { reconcileEstablishedPlatformMe } from './establishedMeValidation.js';
import {
  clearLastCompletedPlatformMe,
  getLastCompletedPlatformMe,
  getPlatformMeInFlight,
} from './fetchPlatformMe.js';
import {
  clearCustomerSessionEstablished,
  markCustomerSessionEstablished,
} from './customerSessionEstablishedLatch.js';
import { clearGuestSessionLatch } from './guestSessionLatch.js';
import {
  beginLoginLand,
  endLoginLand,
  isLoginLandInFlight,
} from './loginLandGate.js';
import {
  giveUpOidcLandAggregateBudget,
  hasOidcLandQuery,
  noteOidcLandAggregateStart,
  OIDC_LAND_AGGREGATE_BUDGET_MS,
} from './oidcCookieLand.js';
import {
  runOidcCookieLandMount,
  runOidcCookieLandVisibilityBonus,
} from './runOidcCookieLandMount.js';
import { runCoalescedPeerMeRevalidate } from './peerMeRevalidate.js';
import { shouldHonorAuthBusMessage } from './shouldHonorAuthBusMessage.js';
import type { CustomerSession } from './customerSessionTypes.js';
import { triggerConsentPromotion } from '../consent/consentPromotionRegistry.js';

export type { CustomerSession } from './customerSessionTypes.js';

/** Last-resort boot settle when `/me` + refresh never complete (hung proxy / offline tab). */
export const CUSTOMER_SESSION_BOOT_HYDRATION_WATCHDOG_MS = 12_000;

interface CustomerSessionContextValue {
  session: CustomerSession | null;
  sessionHydrated: boolean;
  establishSession: (session: CustomerSession) => void;
  applyHydratedSession: (
    session: CustomerSession | null,
    source: CustomerSessionApplySource,
  ) => void;
}

const CustomerSessionContext = createContext<CustomerSessionContextValue | null>(null);

interface CustomerSessionProviderProps {
  children: ReactNode;
}

export function CustomerSessionProvider({
  children,
}: CustomerSessionProviderProps): JSX.Element {
  const config = useRuntimeConfig();
  const tenantCode = useTenantCode();
  const queryClient = useQueryClient();

  const [session, setSession] = useState<CustomerSession | null>(null);
  const [sessionHydrated, setSessionHydrated] = useState(false);
  const sessionRef = useRef<CustomerSession | null>(null);
  const sessionHydratedRef = useRef(false);
  const bootAbortRef = useRef<AbortController | null>(null);
  /** Bumped on establish / successful identity apply — stale peer null must not wipe newer gens. */
  const sessionGenerationRef = useRef(0);
  const bootAttemptRef = useRef(0);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    sessionHydratedRef.current = sessionHydrated;
  }, [sessionHydrated]);

  const bumpSessionGeneration = useCallback((): void => {
    sessionGenerationRef.current += 1;
  }, []);

  const clearMemorySession = useCallback(() => {
    clearCustomerSessionEstablished();
    clearLastCompletedPlatformMe();
    setSession(null);
  }, []);

  const applyHydratedSession = useCallback(
    (incoming: CustomerSession | null, source: CustomerSessionApplySource) => {
      const next = resolveCustomerSessionUpdate(
        sessionRef.current,
        incoming,
        source,
        hasSameTabExplicitAuth(),
      );
      if (incoming === null) {
        // Land window: never null-wipe a session already established by land.
        // Peer/boot land-refuse transient must not clear mid-land (G1/G3).
        if (isLoginLandInFlight() && sessionRef.current !== null) {
          setSessionHydrated(true);
          return;
        }
        // Null hydrate clears memory; do not treat as product silence latch.
        clearCustomerSessionEstablished();
        if (sessionRef.current !== null) {
          sessionRef.current = null;
          setSession(null);
        }
        setSessionHydrated(true);
        return;
      }
      if (next !== null) {
        clearGuestSessionLatch();
        markCustomerSessionStarted();
        markCustomerSessionEstablished();
        bumpSessionGeneration();
        sessionRef.current = next;
        setSession(next);
      }
      setSessionHydrated(true);
    },
    [bumpSessionGeneration],
  );

  const establishSession = useCallback(
    (next: CustomerSession) => {
      clearSameTabExplicitAuth();
      markSameTabExplicitAuth();
      clearGuestSessionLatch();
      markCustomerSessionStarted();
      markCustomerSessionEstablished();
      bumpSessionGeneration();
      // Sync ref immediately so in-flight peer apply sees the new generation/session
      // before the post-paint useEffect (P1 stale-peer race).
      sessionRef.current = next;
      setSession(next);
      setSessionHydrated(true);
      triggerConsentPromotion(next);
      publishCustomerAuth({
        type: 'login',
        tenantCode,
        // Auth V2 identity cookies are platform-scoped (same as OIDC land + refresh).
        // scope:'tenant' + tenantCode:'__platform__' is dropped by shouldHonor on
        // platform-chrome peers (CDP: bus fires, peer never /me).
        scope: 'platform',
      });
    },
    [bumpSessionGeneration, tenantCode],
  );

  const settleBootAfterAbort = useCallback(async () => {
    // Same-tree session already present (e.g. land establish) — settle ready, keep it.
    if (sessionRef.current !== null) {
      setSessionHydrated(true);
      return;
    }
    // B7 — join shared in-flight `/me` when present; ok must still establish.
    const shared =
      typeof getPlatformMeInFlight === 'function'
        ? getPlatformMeInFlight()
        : null;
    if (shared !== null) {
      try {
        const sharedResult = await shared;
        if (sharedResult.kind === 'ok') {
          applyHydratedSession(
            { customerId: sharedResult.me.customerId },
            'bootstrap',
          );
          return;
        }
      } catch {
        // Shared failed — try last-completed below.
      }
    }
    // Abort-after-shared-complete: inFlight already null but shared proved ok.
    const lastCompleted =
      typeof getLastCompletedPlatformMe === 'function'
        ? getLastCompletedPlatformMe()
        : null;
    if (lastCompleted?.kind === 'ok') {
      applyHydratedSession(
        { customerId: lastCompleted.me.customerId },
        'bootstrap',
      );
      return;
    }
    // P2 — abort during open land with nothing to join: do not paint ready+null
    // (Navigate flash). Land establishSession or post-land boot settles hydration.
    if (isLoginLandInFlight()) {
      return;
    }
    setSessionHydrated(true);
  }, [applyHydratedSession]);

  const runBootMe = useCallback(async () => {
    bootAttemptRef.current += 1;
    const bootAttempt = bootAttemptRef.current;
    // Always one boot `/me` per mount after land window — never skip for guest latch.
    bootAbortRef.current?.abort();
    const controller = new AbortController();
    bootAbortRef.current = controller;

    // G1 — boot when not in land window. Waiting avoids land-refuse `transient`
    // null-wiping a just-landed session (or remount mid-land before cookies probe).
    while (isLoginLandInFlight()) {
      if (controller.signal.aborted) {
        await settleBootAfterAbort();
        return;
      }
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, 0);
      });
    }
    if (controller.signal.aborted) {
      await settleBootAfterAbort();
      return;
    }

    let reconciled: Awaited<ReturnType<typeof reconcileEstablishedPlatformMe>>;
    try {
      reconciled = await reconcileEstablishedPlatformMe(config, {
        signal: controller.signal,
        cookieRestore: true,
      });
    } catch (err) {
      if (
        (err instanceof DOMException && err.name === 'AbortError') ||
        (err instanceof Error && err.name === 'AbortError')
      ) {
        await settleBootAfterAbort();
        return;
      }
      if (
        bootAttempt > 1 &&
        hasSameTabExplicitAuth() &&
        sessionRef.current !== null
      ) {
        setSessionHydrated(true);
        return;
      }
      applyHydratedSession(null, 'bootstrap');
      return;
    }
    if (controller.signal.aborted) {
      await settleBootAfterAbort();
      return;
    }

    if (reconciled.action === 'ok') {
      applyHydratedSession({ customerId: reconciled.me.customerId }, 'bootstrap');
      return;
    }
    // G1 — never null-wipe an established session if land re-armed during await.
    if (isLoginLandInFlight() && sessionRef.current !== null) {
      setSessionHydrated(true);
      return;
    }
    // end (guest / hard revoke) OR keep (boot blip) — same as today's transient hydrate-null.
    // G3 — optional RAM-only transient|ceiling backoff is non-Exit; intentionally omitted (no boot-/me skip across F5).
    if (
      bootAttempt > 1 &&
      hasSameTabExplicitAuth() &&
      sessionRef.current !== null
    ) {
      setSessionHydrated(true);
      return;
    }
    applyHydratedSession(null, 'bootstrap');
  }, [applyHydratedSession, config, settleBootAfterAbort]);

  const runPeerMeRevalidate = useCallback(async (reason: 'login' | 'session-refreshed' | 'visibility' = 'session-refreshed') => {
    // Skip peer `/me` while login land is open — land owns establish; refuse/transient
    // must not race-null an established session (G1).
    if (isLoginLandInFlight()) {
      return;
    }
    // Capture generation at peer start — ignore null apply if land/establish bumps gen after.
    const peerAgainstGeneration = sessionGenerationRef.current;
    await runCoalescedPeerMeRevalidate({
      config,
      reason,
      apply: (result) => {
        if (result.kind === 'ok') {
          applyHydratedSession({ customerId: result.me.customerId }, 'cross-tab-login');
          return;
        }
        // Transient/ceiling: do not wipe an existing session (network blip).
        // Guest stays guest until a later visibility/login probe succeeds.
        if (result.kind === 'transient' || result.kind === 'ceiling') {
          return;
        }
        // P1 stale peer: visibility/focus /me left before Set-Cookie; land established
        // after this probe started — never clear the newer session.
        if (hasSameTabExplicitAuth()) {
          return;
        }
        if (
          sessionRef.current !== null &&
          sessionGenerationRef.current > peerAgainstGeneration
        ) {
          return;
        }
        if (isLoginLandInFlight() && sessionRef.current !== null) {
          return;
        }
        // missing_cred | unauthorized — clear phantom signed-in memory.
        applyHydratedSession(null, 'cross-tab-login');
      },
    });
  }, [applyHydratedSession, config]);

  /**
   * G1 — production OIDC cookie land. BE lands `?oidcLand=1`; this effect owns
   * completeOidcCookieLand → strip (OIDC_LAND_QUERY_CHANGED_EVENT) → hydrate.
   * beginLoginLand so boot `/me` waits (land probe uses forLoginLand).
   */
  useEffect(() => {
    let cancelled = false;
    let landGateArmed = false;
    let aggregateBudgetTimer: number | undefined;
    if (typeof window === 'undefined' || !hasOidcLandQuery(window.location.search)) {
      return;
    }

    noteOidcLandAggregateStart();
    // Arm gate synchronously so boot setTimeout(0) cannot race past land.
    beginLoginLand();
    landGateArmed = true;
    const releaseLandGate = (): void => {
      if (!landGateArmed) {
        return;
      }
      landGateArmed = false;
      endLoginLand();
    };
    aggregateBudgetTimer = window.setTimeout(() => {
      if (cancelled) {
        return;
      }
      if (hasOidcLandQuery(window.location.search)) {
        giveUpOidcLandAggregateBudget();
        releaseLandGate();
        setSessionHydrated(true);
      }
    }, OIDC_LAND_AGGREGATE_BUDGET_MS);

    const run = async (): Promise<void> => {
      try {
        const { handled } = await runOidcCookieLandMount({
          tenantCode,
          apiBaseUrl: config.apiBaseUrl,
          isCancelled: () => cancelled,
        });
        if (cancelled) {
          return;
        }
        if (handled) {
          const last = getLastCompletedPlatformMe();
          if (last?.kind === 'ok') {
            establishSession({ customerId: last.me.customerId });
          } else {
            setSessionHydrated(true);
          }
          return;
        }
        // Soft-guest / force-strip give-up — ready so guards can redirect.
        setSessionHydrated(true);
      } finally {
        releaseLandGate();
      }
    };
    void run();
    return () => {
      cancelled = true;
      if (aggregateBudgetTimer !== undefined) {
        window.clearTimeout(aggregateBudgetTimer);
      }
      releaseLandGate();
    };
  }, [config.apiBaseUrl, establishSession, tenantCode]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void runBootMe();
    }, 0);
    return () => {
      window.clearTimeout(timer);
      bootAbortRef.current?.abort();
    };
  }, [runBootMe]);

  useEffect(() => {
    const watchdog = window.setTimeout(() => {
      if (sessionHydratedRef.current) {
        return;
      }
      bootAbortRef.current?.abort();
      if (sessionRef.current !== null) {
        setSessionHydrated(true);
        return;
      }
      applyHydratedSession(null, 'bootstrap');
    }, CUSTOMER_SESSION_BOOT_HYDRATION_WATCHDOG_MS);
    return () => {
      window.clearTimeout(watchdog);
    };
  }, [applyHydratedSession]);

  useEffect(() => {
    const unsubscribe = customerAuthBus.subscribe((message) => {
      if (
        !shouldHonorAuthBusMessage(message, tenantCode, {
          hasSession: sessionRef.current !== null,
        })
      ) {
        return;
      }

      if (message.type === 'logout') {
        void endCustomerSession({
          tenantCode,
          config,
          queryClient,
          broadcast: false,
          callApi: false,
          reason: 'logout',
          clearMemorySession,
          setSessionHydrated,
        });
        return;
      }

      if (message.type === 'login') {
        void runPeerMeRevalidate('login');
        return;
      }

      if (message.type === 'session-refreshed') {
        void runPeerMeRevalidate('session-refreshed');
      }
    });
    return unsubscribe;
  }, [
    clearMemorySession,
    config,
    queryClient,
    runPeerMeRevalidate,
    tenantCode,
  ]);

  // Best-practice cookie catch-up: guest tab may miss BroadcastChannel while
  // backgrounded, or join a pre-cookie boot `/me`. On focus/visibility, probe once.
  // H3 — while `?oidcLand=1` remains, prefer OIDC visibility land over peer `/me`.
  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      if (
        typeof window !== 'undefined' &&
        hasOidcLandQuery(window.location.search)
      ) {
        void (async () => {
          beginLoginLand();
          try {
            const { handled } = await runOidcCookieLandVisibilityBonus({
              tenantCode,
              apiBaseUrl: config.apiBaseUrl,
            });
            if (handled) {
              const last = getLastCompletedPlatformMe();
              if (last?.kind === 'ok') {
                establishSession({ customerId: last.me.customerId });
              }
            }
          } finally {
            endLoginLand();
          }
        })();
        return;
      }
      if (sessionRef.current !== null) {
        return;
      }
      if (isLoginLandInFlight()) {
        return;
      }
      void runPeerMeRevalidate('visibility');
    };
    const onPageShow = (): void => {
      onVisible();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [
    config.apiBaseUrl,
    establishSession,
    runPeerMeRevalidate,
    tenantCode,
  ]);

  useEffect(() => {
    return registerCustomerAuthSyncDeps({
      tenantCode,
      config,
      queryClient,
      clearMemorySession,
      setSessionHydrated,
    });
  }, [clearMemorySession, config, queryClient, tenantCode]);

  const runSessionTimeout = useCallback(() => {
    void endCustomerSession({
      tenantCode,
      config,
      queryClient,
      broadcast: true,
      callApi: true,
      reason: 'idle',
      clearMemorySession,
      setSessionHydrated,
    });
  }, [clearMemorySession, config, queryClient, tenantCode]);

  useCustomerSessionTimeouts({
    enabled: session !== null,
    config,
    onIdleTimeout: runSessionTimeout,
    onAbsoluteTimeout: runSessionTimeout,
  });

  const value = useMemo<CustomerSessionContextValue>(
    () => ({
      session,
      sessionHydrated,
      establishSession,
      applyHydratedSession,
    }),
    [applyHydratedSession, establishSession, session, sessionHydrated],
  );

  return (
    <CustomerSessionContext.Provider value={value}>
      {children}
    </CustomerSessionContext.Provider>
  );
}

export function useCustomerSession(): CustomerSessionContextValue {
  const ctx = useContext(CustomerSessionContext);
  if (ctx === null) {
    throw new Error('useCustomerSession must be used inside <CustomerSessionProvider>');
  }
  return ctx;
}
